// /onboarding — the role question first (board 25 v2 erp/onboarding/role--default,
// copy verbatim): two big cards. «أنا صاحب النشاط» → the phone (counter ١/٦);
// «عندي رمز دعوة» → the invite code (the counter becomes ١/٤ at once). On the
// web the account does not exist yet, so the question comes before the phone.

import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, Store, type LucideIcon } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { sessionStore } from '@/lib/auth';
import { useLayout } from '@/lib/layout';
import { ROLE, SHELL } from '../copy';
import { ONB, onboardingAvailable } from '../data/flow';
import { Wizard } from '../components/Wizard';
import { onboardingStore, useOnboarding, type Intent } from '../state';

/** «عندي رمز دعوة» is the member path WITH a code (counter ١/٤), even before the code is looked up. */
const MEMBER_PATH = { withCode: true } as const;

/** The launch motion of the mark plays once per browser session. */
const LAUNCHED_KEY = 'erp.onboarding.launched';

function launchedBefore(): boolean {
  try {
    return window.sessionStorage.getItem(LAUNCHED_KEY) === '1';
  } catch {
    return false;
  }
}

function markLaunched(): void {
  try {
    window.sessionStorage.setItem(LAUNCHED_KEY, '1');
  } catch {
    /* storage refused — the motion may play again; harmless */
  }
}

function RoleCard({ selected, onSelect, icon: Icon, title, sub, testId }: { selected: boolean; onSelect: () => void; icon: LucideIcon; title: string; sub: string; testId: string }) {
  return (
    <button type="button" role="radio" aria-checked={selected} className="onb-card" onClick={onSelect} data-testid={testId}>
      <span className="onb-card-icon">
        <Icon aria-hidden="true" />
      </span>
      <span className="onb-card-text">
        <span className="onb-card-title">{title}</span>
        <span className="onb-card-sub">{sub}</span>
      </span>
      <span className="onb-card-radio" aria-hidden="true" />
    </button>
  );
}

export function RoleScreen() {
  const navigate = useNavigate();
  const layout = useLayout();
  const { intent, createdId } = useOnboarding();
  const [launch] = useState(() => !launchedBefore());
  useEffect(() => {
    markLaunched();
  }, []);

  if (!onboardingAvailable()) return <Navigate to="/auth/login" replace />;
  // Mid-wizard after the PIN (a reload): carry on from the account's next step.
  if (sessionStore.isAuthenticated()) {
    return <Navigate to={intent === 'member' ? ONB.join : createdId ? ONB.done : ONB.name} replace />;
  }

  const pick = (next: Intent) => onboardingStore.set({ intent: next });

  const submit = () => {
    if (onboardingStore.get().intent === 'member') {
      onboardingStore.set({ intent: 'member', invite: null });
      navigate(ONB.join);
    } else {
      navigate(ONB.phone);
    }
  };

  const mark = launch ? 'launch' : 'static';
  return (
    <Wizard
      screen="role"
      testId="onb-role"
      state="default"
      path={intent === 'member' ? MEMBER_PATH : undefined}
      question={ROLE.title}
      sub={ROLE.sub}
      onSubmit={submit}
      panel={{ phase: 'identity', sub: 'role', mark }}
      mobileMark={mark}
      primary={
        <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={layout === 'phone'} data-testid="onb-continue">
          {SHELL.continue}
        </PrimaryAction>
      }
      tertiary={
        <Link to="/auth/login" className="onb-tert" data-testid="onb-login-link">
          {ROLE.haveAccount}
        </Link>
      }
    >
      <div className="onb-cards" role="radiogroup" aria-label={ROLE.title}>
        <RoleCard selected={intent === 'owner'} onSelect={() => pick('owner')} icon={Store} title={ROLE.owner} sub={ROLE.ownerSub} testId="onb-role-owner" />
        <RoleCard selected={intent === 'member'} onSelect={() => pick('member')} icon={KeyRound} title={ROLE.member} sub={ROLE.memberSub} testId="onb-role-member" />
      </div>
    </Wizard>
  );
}
